import {
    AUDIT_FILTER_MAX_ITEMS,
    isAuditId,
    isChangedField,
    isEntryId,
    isIpAddress,
    isMetadataPath,
    isRouteFilter,
    isStatusCode,
    isStorableText,
    AUDIT_FILTER_MAX_LENGTH as MAX,
} from './filterValidation';
import type { AuditLogFilters } from './types';
import {
    AUDIT_ACTOR_TYPES,
    AUDIT_IMPERSONATION,
    AUDIT_LOG_FILTER_KEYS,
    AUDIT_METHODS,
    AUDIT_OUTCOMES,
} from './types';

/** Every list call writes one of these, so a reviewer's own paging would fill the page. */
export const AUDIT_VIEW_ACTION = 'audit_log.view';

/** The page's one default: audit log views hidden. "Clear all" returns here. */
export const DEFAULT_AUDIT_LOG_FILTERS: Readonly<AuditLogFilters> = Object.freeze({
    excludeActions: [AUDIT_VIEW_ACTION],
});

function text(value: string | undefined, maxLength: number): string | undefined {
    const trimmed = value?.trim();
    if (trimmed === undefined || trimmed === '' || trimmed.length > maxLength) return undefined;
    return isStorableText(trimmed) ? trimmed : undefined;
}

/** Values are matched exactly, so their spaces are kept. */
function exactText(value: string | undefined, maxLength: number): string | undefined {
    if (value === undefined || value === '' || value.length > maxLength) return undefined;
    return isStorableText(value) ? value : undefined;
}

function matching(value: string | undefined, test: (text: string) => boolean): string | undefined {
    return value !== undefined && test(value) ? value : undefined;
}

function auditId(value: number | undefined): number | undefined {
    return value !== undefined && isAuditId(value) ? value : undefined;
}

function validDate(value: Date | undefined): Date | undefined {
    return value instanceof Date && !Number.isNaN(value.getTime()) ? value : undefined;
}

function list(values: readonly string[] | undefined, maxLength: number): string[] | undefined {
    const kept = (values ?? [])
        .map((value) => text(value, maxLength))
        .filter((value): value is string => value !== undefined);
    const unique = [...new Set(kept)].slice(0, AUDIT_FILTER_MAX_ITEMS);
    return unique.length > 0 ? unique : undefined;
}

function known<T extends string>(
    values: readonly T[] | undefined,
    allowed: readonly T[]
): T[] | undefined {
    const unique = allowed.filter((value) => values?.includes(value));
    return unique.length > 0 ? unique : undefined;
}

/**
 * The filters as the backend will accept them. Blank, oversized and malformed values are
 * dropped rather than sent, because a 400 can only show as a generic load error. So are
 * the filters that only mean something next to another one: the backend refuses a
 * changed value without its field and a request-data value without its path, and an
 * actor id or a target id is ambiguous without exactly one type. Asking for audit log
 * views by name also lifts the default that hides them, so the two never contradict.
 */
export function normalizeAuditLogFilters(filters: AuditLogFilters): AuditLogFilters {
    const next: AuditLogFilters = {
        from: validDate(filters.from),
        to: validDate(filters.to),
        entryId: matching(text(filters.entryId, MAX.entryId), isEntryId),
        actorEmail: text(filters.actorEmail, MAX.actorEmail),
        actorType: known(filters.actorType, AUDIT_ACTOR_TYPES),
        actorUserId: auditId(filters.actorUserId),
        impersonation: AUDIT_IMPERSONATION.find((value) => value === filters.impersonation),
        impersonatorUserId: auditId(filters.impersonatorUserId),
        impersonatorEmail: text(filters.impersonatorEmail, MAX.impersonatorEmail),
        actions: list(filters.actions, MAX.action),
        action: text(filters.action, MAX.action),
        excludeActions: list(filters.excludeActions, MAX.action),
        entityType: list(filters.entityType, MAX.entityType),
        entityId: text(filters.entityId, MAX.entityId),
        entityLabel: text(filters.entityLabel, MAX.entityLabel),
        clientId: auditId(filters.clientId),
        locationId: auditId(filters.locationId),
        cageId: auditId(filters.cageId),
        outcome: known(filters.outcome, AUDIT_OUTCOMES),
        statusCode:
            filters.statusCode !== undefined && isStatusCode(filters.statusCode)
                ? filters.statusCode
                : undefined,
        requestId: text(filters.requestId, MAX.requestId),
        ip: matching(text(filters.ip, MAX.ip), isIpAddress),
        userAgent: text(filters.userAgent, MAX.userAgent),
        method: known(filters.method, AUDIT_METHODS),
        route: matching(text(filters.route, MAX.route), isRouteFilter),
        changedField: matching(text(filters.changedField, MAX.changedField), isChangedField),
        changedFrom: exactText(filters.changedFrom, MAX.value),
        changedTo: exactText(filters.changedTo, MAX.value),
        metadataPath: matching(text(filters.metadataPath, MAX.metadataPath), isMetadataPath),
        metadataValue: exactText(filters.metadataValue, MAX.value),
    };
    if (next.changedField === undefined) {
        next.changedFrom = undefined;
        next.changedTo = undefined;
    }
    if (next.metadataPath === undefined) next.metadataValue = undefined;
    if (next.actorType?.length !== 1) next.actorUserId = undefined;
    if (next.entityType?.length !== 1) next.entityId = undefined;
    if (next.actions?.includes(AUDIT_VIEW_ACTION)) {
        next.excludeActions = list(
            next.excludeActions?.filter((action) => action !== AUDIT_VIEW_ACTION),
            MAX.action
        );
    }
    for (const key of AUDIT_LOG_FILTER_KEYS) {
        if (next[key] === undefined) delete next[key];
    }
    return next;
}

export function areAuditViewsHidden(filters: AuditLogFilters): boolean {
    return normalizeAuditLogFilters(filters).excludeActions?.includes(AUDIT_VIEW_ACTION) ?? false;
}

export function isDefaultAuditLogFilters(filters: AuditLogFilters): boolean {
    const normalized = normalizeAuditLogFilters(filters);
    const active = AUDIT_LOG_FILTER_KEYS.filter((key) => normalized[key] !== undefined);
    return (
        active.length === 1 &&
        normalized.excludeActions?.length === 1 &&
        normalized.excludeActions[0] === AUDIT_VIEW_ACTION
    );
}

/**
 * The filters when the page would send every one of them, else null. A value the page
 * would drop, such as an address the backend cannot match exactly or a user agent past
 * its cap, would make a filter button that does nothing.
 */
export function usableAuditFilter(filters: AuditLogFilters): AuditLogFilters | null {
    const normalized = normalizeAuditLogFilters(filters);
    const keepsEvery = AUDIT_LOG_FILTER_KEYS.every(
        (key) => filters[key] === undefined || normalized[key] !== undefined
    );
    return keepsEvery ? filters : null;
}
